// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
    Autocomplete,
    Card,
    CircularProgress,
    Fade,
    Grid,
    Icon,
    Input,
    InputLabel,
    Modal,
    TextField,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";

import { gql, useMutation, useLazyQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import dayjs from "dayjs";
import CardSelection from "../ModalNewProducto/components/CardSelection";
import rgba from "assets/theme/functions/rgba";
import { getUproColor } from "utils/colors";
import { Collapse } from "@mui/material";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "90%", sm: "37%" },
    overflowY: "auto",
    p: 3,
};

export default function ModalAddOT({ open, handleClose, refetch, selectedOT, selected = null }) {
    const [ordenTrabajo, setOrdenTrabajo] = useState({});
    const [productosToSelect, setProductosToSelect] = useState([]);
    const [modo, setModo] = useState(null); // null | "actual" | "presupuesto"
    const [animatedIcon, setAnimatedIcon] = useState(""); // Para animaciones
    const [presupuestos, setPresupuestos] = useState([]);
    const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
    const [eventos, setEventos] = useState([]);
    const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
    const { handleSnackbar } = useContext(MessageManager);

    // Función para manejar la selección de modo con animación
    const handleModeSelection = (newMode) => {
        setAnimatedIcon(newMode);
        if (modo === newMode) {
            setModo(null);
        } else {
            setModo(newMode);
        }
        setTimeout(() => setAnimatedIcon(""), 1000);
    };

    const [getProductos, { loading, error, data }] = useLazyQuery(
        gql`
      query getProductos($filter: JSON) {
        productos(filter: $filter) {
          id
          nombre
          cantidadPorciones
        }
      }
    `,
        {
            variables: {
                filter: { esElaborado: true },
            },
            fetchPolicy: "network-only",
        }
    );

    const [saveOT, { loading: loadingSave }] = useMutation(
        gql`
      mutation saveOrdenTrabajo($input: OrdenTrabajoInput!) {
        saveOrdenTrabajo(input: $input) {
          id
        }
      }
    `
    );

    // Eventos (para el modo "presupuesto")
    const [getEventos, { loading: loadingEventos }] = useLazyQuery(
        gql`
      query getEventos($filter: JSON, $order: Order) {
        eventos(filter: $filter, order: $order) {
          id
          fechaEvento
          estado
        }
      }
    `,
        { fetchPolicy: "network-only" }
    );

    // Presupuestos de eventos (para el modo "presupuesto")
    const [getPresupuestos, { loading: loadingPresupuestos }] = useLazyQuery(
        gql`
      query getPresupuestos($filter: JSON, $order: Order) {
        presupuestoEventos(filter: $filter, order: $order) {
          id
          fecha
          lugar
        }
      }
    `,
        {
            fetchPolicy: "network-only",
        }
    );

    const [getPresupuestoDetalles, { loading: loadingDetalles }] = useLazyQuery(
        gql`
      query getPresupuesto($id: ID!) {
        presupuestoEvento(id: $id) {
          id
          detalles {
            id
            producto {
              id
              nombre
              cantidadPorciones
              esElaborado
            }
          }
        }
      }
    `,
        {
            fetchPolicy: "network-only",
        }
    );

    const handleSave = () => {
        saveOT({
            variables: {
                input: {
                    ...ordenTrabajo,
                    idProducto: parseInt(ordenTrabajo.idProducto),
                    estado: ordenTrabajo?.estado || "Pendiente",
                    cantPorciones: parseInt(ordenTrabajo.cantPorciones),
                    fechaEsperadaEntrega: ordenTrabajo.fechaEsperadaEntrega,
                    // Solo si viene de presupuesto de evento
                    ...(modo === "presupuesto" && presupuestoSeleccionado?.id
                        ? { idPresupuestoEvento: parseInt(presupuestoSeleccionado.id) }
                        : {}),
                },
            },
        })
            .then(() => {
                handleSnackbar("Guardado correctamente", "success");
                setOrdenTrabajo({});
                refetch();
                handleClose();
            })
            .catch(() => {
                handleSnackbar("Error al guardar", "error");
            });
    };

    useEffect(() => {
        if (open) {
            if (selectedOT) {
                setOrdenTrabajo({
                    id: selectedOT?.id,
                    idProducto: selectedOT?.producto?.id,
                    cantPorciones: selectedOT?.cantPorciones,
                    fechaEsperadaEntrega: dayjs(selectedOT?.fechaEsperadaEntrega).format("YYYY-MM-DD"),
                    estado: selectedOT?.estado,
                    observaciones: selectedOT?.observaciones || "",
                });
            } else {
                setOrdenTrabajo({});
            }
            // Inicialmente no mostrar nada hasta elegir modo (salvo si viene `selected`)
            setModo(selected ? "actual" : null);
            setAnimatedIcon(""); // Reset animation state
            setPresupuestoSeleccionado(null);
            setEventoSeleccionado(null);

            if (selected) {
                // Si viene pre-selección, asumimos modo actual y precargamos productos
                getProductos()
                    .then((res) => {
                        setOrdenTrabajo({
                            idProducto: selected?.idProducto,
                            cantPorciones: selected?.total,
                            fechaEsperadaEntrega: dayjs(selected?.fechaPedido).format("YYYY-MM-DD"),
                        });
                        setProductosToSelect(res?.data?.productos);
                    })
                    .catch((error) => {
                        console.error("Error al obtener productos:", error);
                    });
            }
        }
        return () => {
            setProductosToSelect([]);
            setOrdenTrabajo(null);
            setPresupuestos([]);
            setPresupuestoSeleccionado(null);
            setEventos([]);
            setEventoSeleccionado(null);
        };
    }, [open]);

    // Cuando cambia el modo a "presupuesto", precarga los presupuestos
    useEffect(() => {
        if (!open || selectedOT) return; // no aplica en edición
        if (modo === "presupuesto") {
            if (!eventoSeleccionado?.id) {
                setPresupuestoSeleccionado(null);
                setProductosToSelect([]);
                return;
            }
            getPresupuestos({
                variables: {
                    order: { field: "fecha", order: "DESC" },
                },
            })
                .then((res) => {
                    setPresupuestos(res?.data?.presupuestoEventos || []);
                    // Limpiar selección previa
                    setPresupuestoSeleccionado(null);
                    setOrdenTrabajo((prev) => ({ ...prev, idProducto: null }));
                    setProductosToSelect([]);
                })
                .catch((err) => console.error("Error al obtener presupuestos:", err));
        } else if (modo === "actual") {
            // Volver a cargar productos globales
            getProductos()
                .then((res) => setProductosToSelect(res?.data?.productos))
                .catch((err) => console.error("Error al obtener productos:", err));
            setPresupuestoSeleccionado(null);
        }
    }, [modo, open, selectedOT]);
    // Al entrar en modo 'presupuesto', cargar eventos
    useEffect(() => {
        if (!open || selectedOT) return;
        if (modo === "presupuesto") {
            getEventos({ variables: { order: { field: "fechaEvento", order: "DESC" } } })
                .then((res) => setEventos(res?.data?.eventos || []))
                .catch((err) => console.error("Error al obtener eventos:", err));
        }
    }, [modo, open, selectedOT]);

    // Al seleccionar un evento, cargar presupuestos del evento y limpiar selección de producto
    useEffect(() => {
        if (!open || modo !== "presupuesto") return;
        if (!eventoSeleccionado?.id) return;
        setPresupuestoSeleccionado(null);
        setOrdenTrabajo((prev) => ({ ...prev, idProducto: null }));
        setProductosToSelect([]);
        getPresupuestos({
            variables: {
                filter: { idEvento: parseInt(eventoSeleccionado.id) },
                order: { field: "fecha", order: "DESC" },
            },
        })
            .then((res) => setPresupuestos(res?.data?.presupuestoEventos || []))
            .catch((err) => console.error("Error al obtener presupuestos:", err));
    }, [eventoSeleccionado, modo, open]);

    // Al seleccionar un presupuesto, cargar sus detalles para limitar los productos
    useEffect(() => {
        if (!open || !presupuestoSeleccionado?.id) return;
        getPresupuestoDetalles({ variables: { id: presupuestoSeleccionado.id } })
            .then((res) => {
                const detalles = res?.data?.presupuestoEvento?.detalles || [];
                // Mapear productos únicos desde los detalles
                const productos = [];
                const seen = new Set();
                detalles.forEach((d) => {
                    const p = d?.producto;
                    if (p && p.esElaborado && !seen.has(p.id)) {
                        seen.add(p.id);
                        productos.push(p);
                    }
                });
                setProductosToSelect(productos);
                // Si el producto seleccionado actual no está en la lista, limpiarlo
                if (!productos.find((p) => p.id === ordenTrabajo?.idProducto)) {
                    setOrdenTrabajo((prev) => ({ ...prev, idProducto: null }));
                }
            })
            .catch((err) => console.error("Error al obtener detalles del presupuesto:", err));
    }, [presupuestoSeleccionado, open]);

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={handleClose}
            closeAfterTransition
        >
            <Fade in={open}>
                <Card sx={style}>
                    <SoftBox>
                        <SoftBox display="flex" justifyContent="space-between">
                            <SoftBox>
                                <SoftTypography variant="h6">Orden de trabajo</SoftTypography>
                            </SoftBox>
                            <SoftBox display="flex" justifyContent="space-between">
                                <SoftBox>
                                    <Tooltip title="Cerrar" placement="top">
                                        <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                                            close
                                        </Icon>
                                    </Tooltip>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                        <SoftBox py={2}>
                            {!selectedOT && (
                                <SoftBox mb={2}>
                                    <Collapse in={modo === null} unmountOnExit mountOnEnter>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleModeSelection("actual")}
                                                    animatedIcon={animatedIcon === "actual"}
                                                    iconName="build"
                                                    title="Orden de trabajo"
                                                    subtitle="Crear una orden con cualquier plato que requiera una elaboracion. Los platos que se elaboren seran añadidos al stock."
                                                    backGround={
                                                        modo === "actual"
                                                            ? rgba(getUproColor("uproYellow"), 0.5)
                                                            : "rgba(248, 246, 243, 1)"
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <CardSelection
                                                    handleClick={() => handleModeSelection("presupuesto")}
                                                    animatedIcon={animatedIcon === "presupuesto"}
                                                    iconName="event_available"
                                                    title="Orden de Trabajo de Presupuesto"
                                                    subtitle="Orden de trabajo vinculada a un presupuesto de evento, los platos que se elaboren no seran añadidos al stock. Los platos a elegir se limitan a los del presupuesto."
                                                    backGround={
                                                        modo === "presupuesto"
                                                            ? rgba(getUproColor("uproYellow"), 0.5)
                                                            : "rgba(248, 246, 243, 1)"
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>

                                    {/* Botón para volver al modo selección cuando se ha elegido un modo */}
                                    <Collapse in={modo !== null} unmountOnExit mountOnEnter>
                                        <SoftBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2}
                                        >
                                            <SoftTypography variant="h6" fontWeight="medium">
                                                {modo === "actual" ? "Orden de trabajo" : "Orden de Trabajo de Presupuesto"}
                                            </SoftTypography>
                                            <SoftButton
                                                color="secondary"
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setModo(null)}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                }}
                                            >
                                                <Icon sx={{ fontSize: "1rem", mr: 0.5 }}>arrow_back</Icon>
                                                Cambiar tipo
                                            </SoftButton>
                                        </SoftBox>
                                    </Collapse>
                                </SoftBox>
                            )}
                            {(modo !== null || selectedOT) && (
                                <Grid container spacing={1}>
                                    {modo === "presupuesto" && !selectedOT && (
                                        <>
                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="evento">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Evento
                                                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                                            &nbsp;*
                                                        </SoftTypography>
                                                    </SoftTypography>
                                                </InputLabel>
                                                <Autocomplete
                                                    options={eventos || []}
                                                    getOptionLabel={(option) => {
                                                        const fecha = option?.fechaEvento
                                                            ? dayjs(option.fechaEvento).format("DD/MM/YYYY")
                                                            : "";
                                                        return `#${option?.id || ""} ${fecha ? "- " + fecha : ""}`.trim();
                                                    }}
                                                    loading={loadingEventos}
                                                    value={eventoSeleccionado}
                                                    onChange={(e, value) => {
                                                        setEventoSeleccionado(value || null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            InputProps={{ ...params.InputProps }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="presupuesto">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Presupuesto de evento
                                                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                                            &nbsp;*
                                                        </SoftTypography>
                                                    </SoftTypography>
                                                </InputLabel>
                                                <Autocomplete
                                                    options={presupuestos || []}
                                                    getOptionLabel={(option) => {
                                                        const fecha = option?.fecha
                                                            ? dayjs(option.fecha).format("DD/MM/YYYY")
                                                            : "";
                                                        const lugar = option?.lugar || "";
                                                        return `#${option?.id || ""} ${lugar ? "- " + lugar : ""} ${fecha ? "- " + fecha : ""
                                                            }`.trim();
                                                    }}
                                                    loading={loadingPresupuestos}
                                                    value={presupuestoSeleccionado}
                                                    onChange={(e, value) => {
                                                        setPresupuestoSeleccionado(value || null);
                                                    }}
                                                    disabled={!eventoSeleccionado}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            InputProps={{ ...params.InputProps }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <InputLabel htmlFor="nombre">
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                Plato
                                                {!selectedOT && (
                                                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                                        &nbsp;*
                                                    </SoftTypography>
                                                )}
                                            </SoftTypography>
                                        </InputLabel>
                                        {selectedOT || ordenTrabajo?.idProducto ? (
                                            <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                                                <SoftTypography variant="h6" fontWeight="bold">
                                                    <SoftBox display="flex" alignItems="center">
                                                        {(() => {
                                                            const productoSeleccionado = productosToSelect?.find(
                                                                (p) => p.id === ordenTrabajo?.idProducto
                                                            );
                                                            return productoSeleccionado ? (
                                                                <>
                                                                    {`${productoSeleccionado?.id} - ${productoSeleccionado?.nombre} `}
                                                                    <SoftTypography variant="caption" color="secondary">
                                                                        &nbsp;&nbsp;
                                                                        {productoSeleccionado?.cantidadPorciones > 1
                                                                            ? `(Receta para ${productoSeleccionado?.cantidadPorciones} porciones)`
                                                                            : "(Receta para 1 porción)"}
                                                                    </SoftTypography>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {`${selectedOT?.producto?.id} - ${selectedOT?.producto?.nombre} `}
                                                                </>
                                                            );
                                                        })()}
                                                    </SoftBox>
                                                </SoftTypography>
                                                {!selectedOT && !selected && (
                                                    <Tooltip placement="top" title="Cambiar producto">
                                                        <SoftButton
                                                            color="primary"
                                                            circular
                                                            iconOnly
                                                            onClick={() => {
                                                                setOrdenTrabajo((prev) => ({ ...prev, idProducto: null }));
                                                            }}
                                                            sx={{ ml: 2 }}
                                                        >
                                                            <Icon>close</Icon>
                                                        </SoftButton>
                                                    </Tooltip>
                                                )}
                                            </SoftBox>
                                        ) : (
                                            <Autocomplete
                                                options={productosToSelect || []}
                                                getOptionLabel={(option) => option.nombre}
                                                loading={loading || loadingDetalles}
                                                value={
                                                    productosToSelect?.find((p) => p.id === ordenTrabajo.idProducto) || null
                                                }
                                                onChange={(e, value) => {
                                                    setOrdenTrabajo((ordenTrabajo) => ({
                                                        ...ordenTrabajo,
                                                        idProducto: value?.id || null,
                                                    }));
                                                }}
                                                disabled={
                                                    modo === null || (modo === "presupuesto" && !presupuestoSeleccionado)
                                                }
                                                renderOption={(props, option) => {
                                                    const porcionText =
                                                        option.cantidadPorciones > 1 ? "porciones" : "porción";
                                                    return (
                                                        <li {...props}>
                                                            {option.nombre} - &nbsp;
                                                            <strong>
                                                                {porcionText}: {option.cantidadPorciones}
                                                            </strong>
                                                        </li>
                                                    );
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        InputProps={{ ...params.InputProps }}
                                                    />
                                                )}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputLabel htmlFor="nombre">
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                Cantidad
                                                {(() => {
                                                    const productoSeleccionado = productosToSelect?.find(
                                                        (p) => p.id === ordenTrabajo?.idProducto
                                                    );
                                                    const multiplicador = productoSeleccionado?.cantidadPorciones || 1;
                                                    return ordenTrabajo?.cantPorciones &&
                                                        ordenTrabajo?.idProducto &&
                                                        multiplicador > 1 ? (
                                                        <SoftTypography variant="caption" fontWeight="bold" color="secondary">
                                                            &nbsp;&nbsp;rinde {multiplicador * (ordenTrabajo?.cantPorciones || 1)}{" "}
                                                            porciones
                                                        </SoftTypography>
                                                    ) : (
                                                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                                            &nbsp;*
                                                        </SoftTypography>
                                                    );
                                                })()}
                                            </SoftTypography>
                                        </InputLabel>
                                        <SoftInput
                                            type="number"
                                            variant="outlined"
                                            name="cantPorciones"
                                            value={ordenTrabajo?.cantPorciones}
                                            onChange={(e) => {
                                                setOrdenTrabajo((prev) => ({ ...prev, cantPorciones: e.target.value }));
                                            }}
                                            error={
                                                selected?.cantidadTotal
                                                    ? ordenTrabajo?.cantPorciones < selected?.cantidadTotal
                                                    : ordenTrabajo?.cantPorciones < 1
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <InputLabel htmlFor="medioPago">
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                Fecha de ejecución
                                            </SoftTypography>
                                        </InputLabel>
                                        <SoftInput
                                            name="fechaEsperadaEntrega"
                                            type="date"
                                            value={ordenTrabajo?.fechaEsperadaEntrega}
                                            onChange={(e) =>
                                                setOrdenTrabajo((ordenTrabajo) => ({
                                                    ...ordenTrabajo,
                                                    fechaEsperadaEntrega: e.target.value,
                                                }))
                                            }
                                            error={dayjs(ordenTrabajo?.fechaEsperadaEntrega).isBefore(dayjs(), "day")}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputLabel htmlFor="observaciones">
                                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                Observaciones&nbsp;
                                            </SoftTypography>
                                            <SoftTypography variant="caption" fontWeight="light">
                                                (Opcional)
                                            </SoftTypography>
                                        </InputLabel>
                                        <SoftInput
                                            placeholder="Observaciones"
                                            variant="outlined"
                                            name="observaciones"
                                            value={ordenTrabajo?.observaciones}
                                            onChange={(e) =>
                                                setOrdenTrabajo((ordenTrabajo) => ({
                                                    ...ordenTrabajo,
                                                    observaciones: e.target.value,
                                                }))
                                            }
                                            error={ordenTrabajo?.observaciones?.length > 255}
                                            fullWidth
                                            multiline
                                            rows={5}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </SoftBox>
                        <SoftBox
                            display={{
                                xs: "flex-row",
                                sm: "flex",
                            }}
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <SoftBox
                                display={{
                                    xs: "flex-row",
                                    sm: "flex",
                                }}
                                justifyContent="end"
                                alignItems="center"
                            >
                                <SoftBox
                                    mr={{
                                        xs: 0,
                                        sm: 1,
                                    }}
                                    mt={{
                                        xs: 2,
                                        sm: 0,
                                    }}
                                >
                                    <SoftButton
                                        color="primary"
                                        circular
                                        size="small"
                                        onClick={() => {
                                            handleClose();
                                            setOrdenTrabajo({});
                                        }}
                                        fullWidth={{
                                            xs: true,
                                            sm: false,
                                        }}
                                        disabled={loadingSave}
                                    >
                                        <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                                        &nbsp;Cancelar
                                    </SoftButton>
                                </SoftBox>
                                <SoftBox
                                    mt={{
                                        xs: 2,
                                        sm: 0,
                                    }}
                                >
                                    <SoftButton
                                        color="uproGreen"
                                        circular
                                        size="small"
                                        fullWidth={{
                                            xs: true,
                                            sm: false,
                                        }}
                                        onClick={handleSave}
                                        disabled={
                                            loadingSave ||
                                            !ordenTrabajo?.idProducto ||
                                            !ordenTrabajo?.cantPorciones ||
                                            (selected?.cantidadTotal
                                                ? ordenTrabajo?.cantPorciones < selected?.cantidadTotal
                                                : ordenTrabajo?.cantPorciones < 1) ||
                                            dayjs(ordenTrabajo?.fechaEsperadaEntrega).isBefore(dayjs(), "day") ||
                                            ordenTrabajo?.fechaEsperadaEntrega == null ||
                                            (modo === "presupuesto" && !presupuestoSeleccionado?.id)
                                        }
                                    >
                                        {loadingSave ? (
                                            <CircularProgress size={15} color="inherit" />
                                        ) : (
                                            <Icon sx={{ fontWeight: "light" }}>save</Icon>
                                        )}
                                        &nbsp;Guardar
                                    </SoftButton>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </Card>
            </Fade>
        </Modal>
    );
}

ModalAddOT.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    refetch: PropTypes.func,
    selectedCategoria: PropTypes.object,
    selectedOT: PropTypes.any,
    selected: PropTypes.any,
};
